import { NavLink, useLocation } from "react-router-dom";
import { HEADER, NAV } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";

const navItems = [
  { to: "/dashboard", label: "Overview", testid: NAV.dashboardLink, enabled: true },
  { to: "/brands", label: "Brands", testid: NAV.brandsLink, enabled: true },
  { to: "/brand-kit", label: "Brand Kit", testid: NAV.brandKitLink, enabled: true },
  { to: "/brand-programs", label: "Brand Programs", testid: NAV.brandProgramsLink, enabled: true },
  { to: "/design-requests", label: "Studio", testid: NAV.designRequestsLink, enabled: true },
  { to: "/my-documents", label: "My Projects", testid: NAV.myDocumentsLink, enabled: true },
  { to: "/approvals", label: "Approvals", testid: NAV.approvalsLink, enabled: true },
  { to: "/reporting", label: "Reporting", testid: NAV.reportingLink, enabled: false, milestone: "M6" },
  { to: "/settings", label: "Settings", testid: NAV.settingsLink, enabled: false, milestone: "M0+" },
];

// Custom matchers — brand sub-routes should light up their parent nav item.
// Rules and Content now live under Brand Kit, so their sub-routes light it up.
const SUB_NAVS = [
  { match: (p) => /^\/brands\/[^/]+\/brand-kit/.test(p), to: "/brand-kit" },
  { match: (p) => /^\/brands\/[^/]+\/rules/.test(p), to: "/brand-kit" },
  { match: (p) => /^\/brands\/[^/]+\/content/.test(p), to: "/brand-kit" },
  { match: (p) => /^\/brands\/[^/]+\/brand-programs/.test(p), to: "/brand-programs" },
  { match: (p) => /^\/brands\/[^/]+\/design-requests/.test(p), to: "/design-requests" },
];

function isNavActive(item, pathname) {
  const subOverride = SUB_NAVS.find((s) => s.match(pathname));
  if (subOverride) return item.to === subOverride.to;
  if (item.to === "/brands") {
    return pathname === "/brands" || pathname.startsWith("/brands/");
  }
  if (pathname === item.to) return true;
  return pathname.startsWith(item.to + "/");
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  // ProtectedRoute already gates this — if user is null we render nothing
  // and let the parent redirect. Calling navigate() during render triggers
  // a React warning in StrictMode.
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F8F5EE] text-[#1A1F2C] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1A1F2C] text-[#F8F5EE] flex flex-col">
        <div className="px-6 py-7 flex items-center gap-3" data-testid={NAV.logo}>
          <div className="w-7 h-7 rounded-sm bg-[#B5462F]" />
          <div>
            <div className="font-serif text-xl tracking-tight">Artisan</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A8A095]">
              by Pica9
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 mt-2">
          {navItems.map((item) =>
            item.enabled ? (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={() => {
                  const active = isNavActive(item, pathname);
                  return `flex items-center px-4 py-2.5 my-0.5 rounded-md text-sm transition-colors ${
                    active
                      ? "bg-[#F8F5EE] text-[#1A1F2C] font-medium"
                      : "text-[#D8D2C5] hover:bg-[#2A2F3C]"
                  }`;
                }}
              >
                {item.label}
              </NavLink>
            ) : (
              <div
                key={item.to}
                data-testid={item.testid}
                className="flex items-center justify-between px-4 py-2.5 my-0.5 text-sm text-[#6B6358] cursor-not-allowed"
                title={`Available in ${item.milestone}`}
              >
                <span>{item.label}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B6358]">
                  {item.milestone}
                </span>
              </div>
            ),
          )}
        </nav>

        <div className="px-6 py-5 border-t border-[#2A2F3C] text-[10px] uppercase tracking-[0.2em] text-[#6B6358]">
          v0.1 &middot; M3 AI Assembly
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 px-8 border-b border-[#1A1F2C]/10 flex items-center justify-end gap-4 bg-[#F8F5EE]">
          <div className="text-right">
            <div className="text-sm font-medium">{user.name}</div>
            <div className="text-xs text-[#6B6358]">
              {user.email}
              {user.is_platform_admin && (
                <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#B5462F]">
                  Platform Admin
                </span>
              )}
            </div>
          </div>
          {user.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-9 h-9 rounded-full"
              data-testid={HEADER.userMenuTrigger}
            />
          ) : (
            <div
              className="w-9 h-9 rounded-full bg-[#B5462F] text-[#F8F5EE] flex items-center justify-center text-sm font-medium"
              data-testid={HEADER.userMenuTrigger}
            >
              {user.name?.[0] || "?"}
            </div>
          )}
          <button
            data-testid={HEADER.logoutButton}
            onClick={logout}
            className="ml-2 text-sm text-[#6B6358] hover:text-[#1A1F2C]"
          >
            Sign out
          </button>
        </header>

        <main className="flex-1 px-8 py-10 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
